/* COL lane: client-side filter, sort and progressive reveal for NEM collection and search grids. */
(() => {
  if (customElements.get('nem-col-grid')) return;

  const SIZE_ORDER = ['xx-small', 'x-small', 'xs', 'small', 's', 'medium', 'm', 'large', 'l', 'x-large', 'xl', 'xx-large', 'xxl'];
  const SIZE_SHORT = { 'x-small': 'XS', 'small': 'S', 'medium': 'M', 'large': 'L', 'x-large': 'XL', 'xx-large': 'XXL' };
  const sizeRank = (v) => {
    const k = v.toLowerCase();
    const i = SIZE_ORDER.indexOf(k);
    if (i > -1) return i;
    const n = parseFloat(k);
    if (isNaN(n)) return 900;
    return (k.includes('x') ? 500 : 100) + n; /* waist x inseam after chest sizes */
  };
  const PRICE_BANDS = [
    { id: 'u100', label: 'Under $100', min: 0, max: 10000 },
    { id: '100-200', label: '$100 to $200', min: 10000, max: 20000 },
    { id: '200-350', label: '$200 to $350', min: 20000, max: 35000 },
    { id: '350-500', label: '$350 to $500', min: 35000, max: 50000 },
    { id: 'o500', label: '$500 and over', min: 50000, max: Infinity }
  ];
  const COLOUR_FILL = {
    Navy: '#1f2a3c', Green: '#55573a', Black: '#1c1c1c', Brown: '#5a4130', Beige: '#c4b393', Cream: '#ebe4d2', White: '#faf8f2',
    Blue: '#5d7a99', Grey: '#8d8b86', Pink: '#d8a9a6', Red: '#9b2d2a', Orange: '#c46a35', Purple: '#7a6178',
    Pattern: 'conic-gradient(#2f4a35 0 25%, #8f2f2b 0 50%, #1f2a3c 0 75%, #c9b99a 0)', Other: '#bcb2a0'
  };
  const COLOUR_LABEL = { Pattern: 'Tartan & pattern' };
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  class NemColGrid extends HTMLElement {
    connectedCallback() {
      this.perPage = parseInt(this.dataset.perPage || '24', 10);
      this.limit = this.perPage;
      this.query = (this.dataset.query || '').toLowerCase().trim();
      this.grid = this.querySelector('[data-grid]');
      this.pages = parseInt(this.dataset.pages || '1', 10);
      this.page = parseInt(this.dataset.page || '1', 10);
      this.nextPage = this.page + 1;
      this.allLoaded = this.pages <= 1 || this.page > 1 || !this.dataset.sectionId;
      this.totalAll = parseInt(this.dataset.total || '0', 10);
      if (!this.allLoaded) { const nav = this.querySelector('[data-pages-nav]'); if (nav) nav.hidden = true; }
      this.items = [];
      this.addItems(Array.from(this.grid.children));
      this.state = { cat: new Set(), type: new Set(), size: new Set(), colour: new Set(), price: new Set(), avail: false, sort: 'featured' };
      this.readUrl();
      this.bind();
      if (this.hasState()) this.loadAll().then(() => { this.buildGroups(); this.apply(false); });
      else {
        this.buildGroups(); this.apply(false);
        /* Load the remaining pages quietly so one-tile-per-style grouping and the count cover the whole collection.
           Waits until the page has loaded and the phone is idle, so it never competes with the first screen.
           Show more, sort and filters still load at once (loadAll is shared, so nothing is fetched twice). */
        if (!this.allLoaded) {
          const go = () => { if (!this.allLoaded) this.loadAll().then(() => { this.buildGroups(); this.apply(false); }); };
          const idle = () => ('requestIdleCallback' in window ? window.requestIdleCallback(go, { timeout: 4000 }) : setTimeout(go, 1500));
          if (document.readyState === 'complete') idle(); else window.addEventListener('load', idle, { once: true });
        }
      }
    }

    addItems(lis, pg) {
      const base = this.items.length;
      const block = pg || this.page;
      lis.forEach((li, k) => {
        const c = li.querySelector('[data-nem-card]');
        const d = c ? c.dataset : {};
        const it = {
          li, i: base + k, block,
          title: (d.title || '').toLowerCase(),
          price: parseInt(d.price || '0', 10),
          avail: d.avail === '1',
          img: d.img !== '0',
          sizes: (d.sizes || '').split('|').filter(Boolean),
          colour: d.colour || '',
          cat: d.cat || '',
          type: d.type || '',
          created: parseInt(d.created || '0', 10),
          group: d.group || String(base + k),
          pos: parseInt(li.dataset.pos || '0', 10),
          hit: 0
        };
        if (this.query) it.hit = it.title.includes(this.query) ? 1 : 0;
        this.items.push(it);
      });
    }

    hasState() {
      const s = this.state;
      return s.avail || s.sort !== 'featured' || ['cat', 'type', 'size', 'colour', 'price'].some((k) => s[k].size);
    }

    async loadPage(n) {
      const url = new URL(location.href);
      ['cat', 'ptype', 'size', 'colour', 'price', 'instock', 'sort'].forEach((k) => url.searchParams.delete(k));
      url.searchParams.set('page', n);
      url.searchParams.set('section_id', this.dataset.sectionId);
      const res = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!res.ok) throw new Error('page ' + n);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const lis = Array.from(doc.querySelectorAll('[data-grid] > li'));
      lis.forEach((li) => { li.hidden = true; this.grid.appendChild(li); });
      this.addItems(lis, n);
    }

    async loadPageRetry(n) {
      try { await this.loadPage(n); }
      catch (e) { await new Promise((r) => setTimeout(r, 900)); await this.loadPage(n); }
      this.nextPage = n + 1;
      if (this.nextPage > this.pages) this.allLoaded = true;
    }

    setFailed(failed) {
      this.failed = failed;
      const lab = this.querySelector('[data-more] .button__label');
      if (lab) lab.textContent = failed ? 'Load more' : 'Show more';
      const msg = this.querySelector('[data-more-error]');
      if (msg) msg.hidden = !failed;
    }

    loadAll() {
      if (this.allLoaded) return Promise.resolve();
      if (this.loading) return this.loading;
      this.setAttribute('aria-busy', 'true');
      this.loading = (async () => {
        try { while (!this.allLoaded) await this.loadPageRetry(this.nextPage); this.setFailed(false); }
        catch (e) { this.setFailed(true); }
        this.removeAttribute('aria-busy');
        this.groupsBuilt = false;
        this.loading = null;
      })();
      return this.loading;
    }

    readUrl() {
      const p = new URLSearchParams(location.search);
      /* URL key for the product-type facet is 'ptype': Shopify search uses 'type' itself (type=product). */
      ['cat', 'type', 'size', 'colour', 'price'].forEach((k) => {
        p.getAll(k === 'type' ? 'ptype' : k).forEach((v) => this.state[k].add(v));
      });
      this.state.avail = p.get('instock') === '1';
      const s = p.get('sort');
      if (s) this.state.sort = s;
      const sel = this.querySelector('[data-sort]');
      if (sel) sel.value = this.state.sort;
      if (sel && sel.value !== this.state.sort) { this.state.sort = 'featured'; sel.value = 'featured'; }
    }

    writeUrl() {
      const p = new URLSearchParams(location.search);
      ['cat', 'ptype', 'size', 'colour', 'price', 'instock', 'sort'].forEach((k) => p.delete(k));
      ['cat', 'type', 'size', 'colour', 'price'].forEach((k) => this.state[k].forEach((v) => p.append(k === 'type' ? 'ptype' : k, v)));
      if (this.state.avail) p.set('instock', '1');
      if (this.state.sort !== 'featured') p.set('sort', this.state.sort);
      const q = p.toString();
      history.replaceState(history.state, '', location.pathname + (q ? '?' + q : '') + location.hash);
    }

    count(key, fn) {
      const m = new Map();
      this.items.forEach((it) => fn(it).forEach((v) => { if (v) m.set(v, (m.get(v) || 0) + 1); }));
      return m;
    }

    buildGroups() {
      const host = this.querySelector('[data-groups]');
      if (!host) return;
      this.groupsBuilt = this.allLoaded;
      const groups = [];
      const cats = this.count('cat', (it) => [it.cat]);
      const types = this.count('type', (it) => [it.type]);
      if (cats.size > 1) groups.push(this.listGroup('cat', 'Category', [...cats].sort((a, b) => b[1] - a[1])));
      if (types.size > 1 && (cats.size <= 1 || types.size <= 14)) groups.push(this.listGroup('type', 'Type', [...types].sort((a, b) => b[1] - a[1])));
      const sizes = this.count('size', (it) => it.sizes);
      if (sizes.size > 0) groups.push(this.sizeGroup([...sizes].sort((a, b) => sizeRank(a[0]) - sizeRank(b[0]))));
      const colours = this.count('colour', (it) => [it.colour]);
      if (colours.size > 1) groups.push(this.listGroup('colour', 'Colour', [...colours].sort((a, b) => b[1] - a[1]), true));
      const prices = new Map();
      PRICE_BANDS.forEach((b) => {
        const n = this.items.filter((it) => it.price >= b.min && it.price < b.max).length;
        if (n) prices.set(b.id, n);
      });
      if (prices.size > 1) groups.push(this.listGroup('price', 'Price', PRICE_BANDS.filter((b) => prices.has(b.id)).map((b) => [b.id, prices.get(b.id), b.label])));
      const inStock = this.items.filter((it) => it.avail).length;
      if (inStock && inStock < this.items.length) {
        groups.push(`<details class="nem-col__group" open><summary>Availability</summary><ul class="nem-col__opts" role="list">
          <li><label class="nem-col__opt"><input type="checkbox" data-avail ${this.state.avail ? 'checked' : ''}><span class="nem-col__box"></span>In stock only<span class="nem-col__opt-n">${inStock}</span></label></li></ul></details>`);
      }
      host.innerHTML = groups.join('');
      if (!groups.length) {
        const btn = this.querySelector('[data-filter-open]');
        if (btn) btn.hidden = true;
      }
    }

    listGroup(key, title, entries, swatch) {
      const rows = entries.map(([v, n, label]) => {
        const lab = label || (key === 'colour' ? (COLOUR_LABEL[v] || v) : v);
        const sw = swatch ? `<span class="nem-swatch" style="--sw:${COLOUR_FILL[v] || COLOUR_FILL.Other}" aria-hidden="true"></span>` : '';
        return `<li><label class="nem-col__opt"><input type="checkbox" data-key="${key}" value="${esc(v)}" ${this.state[key].has(v) ? 'checked' : ''}>${swatch ? '' : '<span class="nem-col__box"></span>'}${sw}${esc(lab)}<span class="nem-col__opt-n">${n}</span></label></li>`;
      }).join('');
      const open = this.state[key].size > 0 || key === 'cat' || key === 'type' ? ' open' : '';
      return `<details class="nem-col__group"${open}><summary>${title}</summary><ul class="nem-col__opts" role="list">${rows}</ul></details>`;
    }

    sizeGroup(entries) {
      const rows = entries.map(([v]) => {
        const lab = SIZE_SHORT[v.toLowerCase()] || v;
        return `<li><label class="nem-col__size" title="${esc(v)}"><input type="checkbox" data-key="size" value="${esc(v)}" ${this.state.size.has(v) ? 'checked' : ''}><span>${esc(lab)}</span></label></li>`;
      }).join('');
      return `<details class="nem-col__group" open><summary>Size</summary><ul class="nem-col__opts nem-col__opts--sizes" role="list">${rows}</ul></details>`;
    }

    bind() {
      this.drawer = this.querySelector('[data-drawer]');
      this.openBtn = this.querySelector('[data-filter-open]');
      this.openBtn && this.openBtn.addEventListener('click', () => this.open());
      this.querySelectorAll('[data-filter-close]').forEach((b) => b.addEventListener('click', () => this.close()));
      this.querySelectorAll('[data-clear]').forEach((b) => b.addEventListener('click', () => this.clear()));
      this.addEventListener('change', (e) => {
        const t = e.target;
        if (t.matches('[data-sort]')) { this.state.sort = t.value; this.limit = this.perPage; this.loadAll().then(() => this.apply(true)); return; }
        if (t.matches('[data-avail]')) { this.state.avail = t.checked; this.limit = this.perPage; this.apply(true); return; }
        if (t.dataset && t.dataset.key) {
          const set = this.state[t.dataset.key];
          t.checked ? set.add(t.value) : set.delete(t.value);
          this.limit = this.perPage;
          this.apply(true);
        }
      });
      const more = this.querySelector('[data-more]');
      more && more.addEventListener('click', async () => {
        const first = this.visible[this.limit];
        this.limit += this.perPage;
        if (!this.allLoaded && this.visible.length < this.limit) {
          more.disabled = true;
          this.setAttribute('aria-busy', 'true');
          try {
            if (this.hasState()) await this.loadAll(); else await this.loadPageRetry(this.nextPage);
            this.setFailed(false);
          } catch (e) { this.setFailed(true); this.limit -= this.perPage; }
          this.removeAttribute('aria-busy');
          more.disabled = false;
        }
        this.apply(false);
        const target = first || this.visible[this.limit - this.perPage];
        if (target) { const a = target.li.querySelector('a[href]:not([tabindex="-1"])'); a && a.focus({ preventScroll: true }); }
      });
      this.querySelector('[data-chips]')?.addEventListener('click', (e) => {
        const b = e.target.closest('[data-chip]');
        if (!b) return;
        const [k, v] = [b.dataset.chip, b.dataset.value];
        if (k === 'avail') this.state.avail = false; else this.state[k].delete(v);
        this.syncInputs();
        this.limit = this.perPage;
        this.apply(true);
      });
      this.onKey = (e) => { if (e.key === 'Escape') this.close(); };
    }

    async open() {
      if (!this.allLoaded) {
        this.openBtn.setAttribute('aria-busy', 'true');
        await this.loadAll();
        this.openBtn.removeAttribute('aria-busy');
      }
      if (!this.groupsBuilt) { this.buildGroups(); this.apply(false); }
      this.drawer.hidden = false;
      document.documentElement.classList.add('nem-col-lock');
      this.openBtn.setAttribute('aria-expanded', 'true');
      document.addEventListener('keydown', this.onKey);
      const f = this.drawer.querySelector('.nem-col__close');
      f && f.focus();
    }

    close() {
      if (!this.drawer || this.drawer.hidden) return;
      this.drawer.hidden = true;
      document.documentElement.classList.remove('nem-col-lock');
      this.openBtn.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', this.onKey);
      this.openBtn.focus();
    }

    clear() {
      ['cat', 'type', 'size', 'colour', 'price'].forEach((k) => this.state[k].clear());
      this.state.avail = false;
      this.syncInputs();
      this.limit = this.perPage;
      this.apply(true);
    }

    syncInputs() {
      this.querySelectorAll('[data-key]').forEach((i) => { i.checked = this.state[i.dataset.key].has(i.value); });
      const a = this.querySelector('[data-avail]');
      if (a) a.checked = this.state.avail;
    }

    matches(it) {
      const s = this.state;
      if (s.cat.size && !s.cat.has(it.cat)) return false;
      if (s.type.size && !s.type.has(it.type)) return false;
      if (s.colour.size && !s.colour.has(it.colour)) return false;
      if (s.size.size && !it.sizes.some((z) => s.size.has(z))) return false;
      if (s.price.size && !PRICE_BANDS.some((b) => s.price.has(b.id) && it.price >= b.min && it.price < b.max)) return false;
      if (s.avail && !it.avail) return false;
      return true;
    }

    sorted(list) {
      const by = {
        featured: (a, b) => (b.hit - a.hit) || (a.block - b.block) || ((a.pos && b.pos) ? a.pos - b.pos : 0) || (b.avail - a.avail) || (b.img - a.img) || (a.i - b.i),
        newest: (a, b) => (b.created - a.created) || (a.i - b.i),
        'price-asc': (a, b) => (a.price - b.price) || (a.i - b.i),
        'price-desc': (a, b) => (b.price - a.price) || (a.i - b.i),
        title: (a, b) => a.title.localeCompare(b.title) || (a.i - b.i)
      }[this.state.sort] || ((a, b) => a.i - b.i);
      return list.slice().sort(by);
    }

    apply(push) {
      /* One tile per style: colours of the same style share a group; the first match in sort order represents it
         (so a colour filter shows that colour's tile). The other colours stay reachable through the tile's swatches. */
      const seen = new Set();
      const vis = this.sorted(this.items.filter((it) => this.matches(it))).filter((it) => {
        if (seen.has(it.group)) return false;
        seen.add(it.group); return true;
      });
      this.visible = vis;
      const frag = document.createDocumentFragment();
      vis.forEach((it, idx) => { it.li.hidden = idx >= this.limit; frag.appendChild(it.li); });
      this.items.filter((it) => !vis.includes(it)).forEach((it) => { it.li.hidden = true; frag.appendChild(it.li); });
      this.grid.appendChild(frag);

      /* Count = every matching piece (each colour counts once; the grid shows one tile per style). Same number before and after
         the rest of the collection loads, so it never changes in front of the shopper. */
      const total = (!this.allLoaded && !this.hasState()) ? Math.max(this.totalAll, this.items.length) : this.items.filter((it) => this.matches(it)).length;
      const word = total === 1 ? 'piece' : 'pieces';
      const countEl = this.querySelector('[data-count]');
      if (countEl) countEl.textContent = `${total} ${word}`;
      const apply = this.querySelector('[data-apply-label]');
      if (apply) apply.textContent = total ? `Show ${total} ${word}` : 'No matches';
      const empty = this.querySelector('[data-empty]');
      if (empty) empty.hidden = total > 0;

      const wrap = this.querySelector('[data-more-wrap]');
      if (wrap) {
        const shown = Math.min(this.limit, vis.length, total);
        wrap.hidden = total <= this.limit && !this.failed;
        const prog = this.querySelector('[data-progress]');
        if (prog) prog.textContent = `Showing ${shown} of ${total}`;
        const bar = this.querySelector('[data-bar]');
        if (bar) bar.style.width = total ? `${(shown / total) * 100}%` : '0';
      }

      const active = [];
      ['cat', 'type', 'size', 'colour', 'price'].forEach((k) => this.state[k].forEach((v) => {
        let label = v;
        if (k === 'price') label = (PRICE_BANDS.find((b) => b.id === v) || {}).label || v;
        if (k === 'colour') label = COLOUR_LABEL[v] || v;
        if (k === 'size') label = 'Size ' + (SIZE_SHORT[v.toLowerCase()] || v);
        active.push(`<button type="button" class="nem-col__chip" data-chip="${k}" data-value="${esc(v)}" aria-label="Remove filter ${esc(label)}">${esc(label)}<svg aria-hidden="true" viewBox="0 0 10 10" stroke="currentColor" stroke-width="1.2"><path d="M1 1l8 8M9 1L1 9"/></svg></button>`);
      }));
      if (this.state.avail) active.push('<button type="button" class="nem-col__chip" data-chip="avail" data-value="1" aria-label="Remove filter In stock only">In stock only<svg aria-hidden="true" viewBox="0 0 10 10" stroke="currentColor" stroke-width="1.2"><path d="M1 1l8 8M9 1L1 9"/></svg></button>');
      const chips = this.querySelector('[data-chips]');
      if (chips) chips.innerHTML = active.join('');
      const badge = this.querySelector('[data-active-count]');
      if (badge) { badge.hidden = !active.length; badge.textContent = active.length; }
      if (push) this.writeUrl();
    }
  }
  customElements.define('nem-col-grid', NemColGrid);
})();
